import styled from 'styled-components';

// Color constants
export const COLORS = {
  primary: '#6CB132',
  primaryDark: '#5a9629',
  primaryDarker: '#4a7d1f',
  secondary: '#F1A73C',
  secondaryDark: '#e89620',
  gold: '#FFD700',
  goldDark: '#FFA500',
  goldAccent: '#b8860b',
  silver: '#C0C0C0',
  silverDark: '#808080',
  silverAccent: '#696969',
  bronze: '#CD7F32',
  bronzeDark: '#8B4513',
  bronzeAlt: '#f59e0b',
  bronzeAltDark: '#d97706',
  text: '#1a1a2e',
  textSecondary: '#64718A',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  background: '#f5f7fa',
  backgroundAlt: '#e8edf4',
  white: '#ffffff',
  error: '#e74c3c',
};

// Page Container
export const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.backgroundAlt} 100%);
  padding-bottom: 4rem;
`;

// Hero Section Styles
export const HeroSection = styled.section`
  padding: 1.5rem 1rem;
  text-align: center;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    top: -50px;
    right: -50px;
  }

  &::after {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    bottom: -30px;
    left: -30px;
  }

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

export const TitleWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

export const TrophyIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  animation: bounce 2s ease-in-out infinite;

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const MainTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

// Loading & Error Styles
export const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${COLORS.text};
`;

export const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(108, 177, 50, 0.2);
  border-top-color: ${COLORS.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${COLORS.error};
  font-size: 1.125rem;
`;

// Mobile Podium Styles
export const MobilePodiumContainer = styled.div`
  display: none;

  @media (max-width: 968px) {
    display: block;
    padding: 1rem;
    margin-top: -2rem;
    position: relative;
    z-index: 10;
  }

  .podium-display {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .podium-item {
    flex: 1;
    max-width: 110px;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.3s ease;
    animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    opacity: 0;
    transform: scale(0.3) translateY(50px);

    &:active {
      opacity: 0.7;
    }

    &.first {
      max-width: 130px;
      animation-delay: 0s;

      .profile-image-wrapper {
        animation-delay: 0.3s;
      }
    }

    &.second {
      animation-delay: 0.15s;

      .profile-image-wrapper {
        animation-delay: 0.45s;
      }
    }

    &.third {
      animation-delay: 0.3s;

      .profile-image-wrapper {
        animation-delay: 0.6s;
      }
    }

    @keyframes popIn {
      0% {
        opacity: 0;
        transform: scale(0.3) translateY(50px);
      }
      60% {
        opacity: 1;
        transform: scale(1.1) translateY(-10px);
      }
      100% {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
  }

  .crown {
    display: block;
    text-align: center;
    font-size: 1.5rem;
    margin-bottom: -0.5rem;
    position: relative;
    z-index: 1;
    animation: crownFloat 3s ease-in-out infinite, crownBounce 0.8s ease-out;
    animation-delay: 0s, 0.3s;

    @keyframes crownFloat {
      0%, 100% {
        transform: translateY(0) rotate(-3deg);
      }
      50% {
        transform: translateY(-8px) rotate(3deg);
      }
    }

    @keyframes crownBounce {
      0% {
        opacity: 0;
        transform: scale(0) rotate(-180deg);
      }
      60% {
        transform: scale(1.3) rotate(10deg);
      }
      100% {
        opacity: 1;
        transform: scale(1) rotate(0deg);
      }
    }
  }

  .podium-card {
    background: white;
    border-radius: 0.75rem 0.75rem 0 0;
    padding: 0.75rem;
    text-align: center;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid ${COLORS.border};

    &.gold {
      border: 2px solid #fbbf24;
      padding-top: 1rem;
    }

    .profile-image-wrapper {
      width: 4rem;
      height: 4rem;
      margin: 0 auto 0.5rem;
      animation: zoomIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      opacity: 0;
      position: relative;
    }

    @keyframes zoomIn {
      0% {
        opacity: 0;
        transform: scale(0);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    .profile-image-skeleton {
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: linear-gradient(
        90deg,
        #f0f0f0 0%,
        #e0e0e0 20%,
        #f0f0f0 40%,
        #f0f0f0 100%
      );
      background-size: 200% 100%;
      animation: shimmer 1.5s ease-in-out infinite;
      z-index: 1;

      @keyframes shimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    }

    .profile-image {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid ${COLORS.border};
      animation: pulse 2s ease-in-out infinite;
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
      position: relative;
      z-index: 2;
    }

    @keyframes pulse {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(108, 177, 50, 0.4);
      }
      50% {
        box-shadow: 0 0 0 8px rgba(108, 177, 50, 0);
      }
    }

    .profile-image-placeholder {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      font-weight: 700;
      color: white;
      background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
      border: 2px solid ${COLORS.border};
      animation: pulse 2s ease-in-out infinite;

      &.gold {
        border-color: #fbbf24;
        animation: goldPulse 2s ease-in-out infinite;

        @keyframes goldPulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.6);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(251, 191, 36, 0);
          }
        }
      }

      &.silver {
        border-color: #d1d5db;
        animation: silverPulse 2s ease-in-out infinite;

        @keyframes silverPulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(209, 213, 219, 0.6);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(209, 213, 219, 0);
          }
        }
      }

      &.bronze {
        border-color: ${COLORS.bronzeAlt};
        animation: bronzePulse 2s ease-in-out infinite;

        @keyframes bronzePulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
          }
        }
      }
    }

    .username {
      font-size: 0.75rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 0.25rem;
      word-break: break-word;
    }

    .city {
      font-size: 0.625rem;
      color: #6b7280;
      margin-bottom: 0.375rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;

      svg {
        width: 0.625rem;
        height: 0.625rem;
      }
    }

    .stats-row {
      display: flex;
      gap: 0.5rem;
      justify-content: center;
      margin-top: 0.25rem;
    }

    .stat-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.125rem;
    }

    .stat-value {
      font-size: 0.75rem;
      font-weight: 700;
      color: #1f2937;
      display: flex;
      align-items: center;
      gap: 0.25rem;

      svg {
        width: 0.65rem;
        height: 0.65rem;
        color: ${COLORS.bronzeAlt};
      }
    }

    .stat-label {
      font-size: 0.5rem;
      color: ${COLORS.textMuted};
      text-transform: uppercase;
      letter-spacing: 0.025em;
      font-weight: 600;
    }
  }

  .podium-base {
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 0.5rem 0.5rem;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);

    span {
      color: white;
      font-weight: 700;
      font-size: 1rem;
      line-height: 1;
    }

    .base-icon {
      color: white;
      width: 30%;
      height: 30%;
      flex-shrink: 0;
      line-height: 1;
    }

    &.gold-base {
      background: linear-gradient(135deg, #fbbf24 0%, ${COLORS.bronzeAlt} 100%);
      height: 5rem;
    }

    &.silver-base {
      background: linear-gradient(135deg, #d1d5db 0%, ${COLORS.textMuted} 100%);
      height: 3.5rem;
    }

    &.bronze-base {
      background: linear-gradient(135deg, ${COLORS.bronzeAlt} 0%, ${COLORS.bronzeAltDark} 100%);
      height: 2.5rem;

      .base-icon {
        width: 40%;
        height: 40%;
      }
    }
  }

  .tap-hint {
    text-align: center;
    font-size: 0.75rem;
    color: ${COLORS.textMuted};
    margin-bottom: 1rem;
  }

  .compact-list {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid ${COLORS.border};
    overflow: hidden;
  }

  .compact-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    transition: background-color 0.2s;

    &:last-child {
      border-bottom: none;
    }

    &:active {
      background-color: #f9fafb;
    }

    .rank-badge {
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      background: #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      span {
        font-size: 0.75rem;
        font-weight: 700;
        color: #7c3aed;
      }
    }

    .user-info {
      flex: 1;
      min-width: 0;

      .username {
        font-size: 0.875rem;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .stats {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 0.75rem;
        color: #6b7280;

        .stat {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          white-space: nowrap;

          &.city {
            color: ${COLORS.textMuted};
          }
        }

        .star-icon {
          color: #fbbf24;
          width: 0.75rem;
          height: 0.75rem;
        }

        svg {
          width: 0.75rem;
          height: 0.75rem;
        }
      }
    }

    .arrow {
      flex-shrink: 0;
      color: #d1d5db;

      svg {
        width: 0.75rem;
        height: 0.75rem;
      }
    }
  }
`;

// Desktop Podium Styles
export const PodiumSection = styled.section`
  max-width: 1200px;
  margin: -2rem auto 3rem;
  padding: 0 1rem;
  position: relative;
  z-index: 10;

  @media (max-width: 968px) {
    display: none;
  }
`;

export const PodiumContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 2rem;
  flex-wrap: wrap;
`;

export const PodiumPlace = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: slideUp 0.6s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
  opacity: 0;

  @keyframes slideUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
    from {
      opacity: 0;
      transform: translateY(30px);
    }
  }

  &.first {
    order: 2;
  }

  &.second {
    order: 1;
    margin-top: 3rem;
  }

  &.third {
    order: 3;
    margin-top: 4rem;
  }
`;

export const Crown = styled.div`
  font-size: 2.5rem;
  margin-bottom: -0.75rem;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(-5deg); }
    50% { transform: translateY(-10px) rotate(5deg); }
  }
`;

export const PlaceNumber = styled.div`
  font-size: 1rem;
  font-weight: 700;
  padding: 0.5rem 1.25rem;
  border-radius: 2rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;

  &.gold {
    background: linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldDark} 100%);
    box-shadow: 0 6px 16px rgba(255, 215, 0, 0.4);
  }

  &.silver {
    background: linear-gradient(135deg, ${COLORS.silver} 0%, ${COLORS.silverDark} 100%);
    box-shadow: 0 6px 16px rgba(192, 192, 192, 0.4);
  }

  &.bronze {
    background: linear-gradient(135deg, ${COLORS.bronze} 0%, ${COLORS.bronzeDark} 100%);
    box-shadow: 0 6px 16px rgba(205, 127, 50, 0.4);
  }
`;

export const PodiumCard = styled.div`
  background: white;
  border-radius: 1.25rem;
  padding: 1.5rem;
  width: 260px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 3px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }

  &.gold-card {
    border-color: ${COLORS.gold};
  }

  &.silver-card {
    border-color: ${COLORS.silver};
  }

  &.bronze-card {
    border-color: ${COLORS.bronze};
  }
`;

export const Avatar = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 auto 0.75rem;
  border: 4px solid transparent;

  &.gold-border {
    border-color: ${COLORS.gold};
  }

  &.silver-border {
    border-color: ${COLORS.silver};
  }

  &.bronze-border {
    border-color: ${COLORS.bronze};
  }
`;

export const UserName = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${COLORS.text};
  text-align: center;
  margin-bottom: 0.25rem;
  word-break: break-word;

  &.champion {
    background: linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldDark} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const CityLabel = styled.div`
  font-size: 0.8rem;
  color: ${COLORS.textSecondary};
  text-align: center;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

export const ChampionBadge = styled.div`
  background: linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldDark} 100%);
  color: white;
  padding: 0.4rem 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.75rem;
`;

export const StatsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

export const StatBadge = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  transition: transform 0.2s ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    transform: translateY(-2px);
  }

  &.gold-stat {
    background: rgba(255, 215, 0, 0.15);
    color: ${COLORS.goldAccent};
    border: 2px solid rgba(255, 215, 0, 0.3);
  }

  &.silver-stat {
    background: rgba(192, 192, 192, 0.25);
    color: ${COLORS.silverAccent};
    border: 2px solid rgba(192, 192, 192, 0.4);
  }

  &.bronze-stat {
    background: rgba(205, 127, 50, 0.15);
    color: ${COLORS.bronzeDark};
    border: 2px solid rgba(205, 127, 50, 0.3);
  }
`;

export const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;

  .qr-button, .profile-button {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;

    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }

  .profile-button {
    background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
    color: white;

    &:hover {
      background: linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primaryDarker} 100%);
    }
  }
`;

export const PodiumBase = styled.div`
  width: 260px;
  padding: 1rem;
  margin-top: 0.75rem;
  border-radius: 0.5rem;
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  color: white;

  &.base-gold {
    background: linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldDark} 100%);
    height: 100px;
  }

  &.base-silver {
    background: linear-gradient(135deg, ${COLORS.silver} 0%, ${COLORS.silverDark} 100%);
    height: 70px;
  }

  &.base-bronze {
    background: linear-gradient(135deg, ${COLORS.bronze} 0%, ${COLORS.bronzeDark} 100%);
    height: 50px;
  }
`;

// Rest of Rankings (Rising Stars) Styles
export const RestSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
`;

export const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${COLORS.text};
  text-align: center;
  margin-bottom: 1.5rem;
`;

export const RestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const RestCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  animation: slideIn 0.4s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
  opacity: 0;
  border: 2px solid transparent;

  @keyframes slideIn {
    to {
      opacity: 1;
      transform: translateX(0);
    }
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(108, 177, 50, 0.2);
    border-color: rgba(108, 177, 50, 0.2);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 1rem;
    padding: 1.25rem;
  }
`;

export const RankSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    position: absolute;
    top: 1.25rem;
    left: 1.25rem;
  }
`;

export const RankBadge = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.125rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(108, 177, 50, 0.3);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid rgba(108, 177, 50, 0.2);
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 0.95rem;
    box-shadow: 0 3px 8px rgba(108, 177, 50, 0.3);

    &::after {
      inset: -3px;
      border-width: 1.5px;
    }
  }
`;

export const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 768px) {
    position: relative;
    padding-left: 60px;
    margin-bottom: 0.75rem;
  }
`;

export const SmallAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 3px solid rgba(108, 177, 50, 0.1);
  box-shadow: 0 2px 8px rgba(108, 177, 50, 0.2);

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1.125rem;
  }
`;

export const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const UserNameSmall = styled.div`
  font-weight: 600;
  color: ${COLORS.text};
  font-size: 1.05rem;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

export const CityLabelSmall = styled.div`
  font-size: 0.75rem;
  color: ${COLORS.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.25rem;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

export const StatsSection = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 0 1rem;
  border-left: 2px solid #f0f0f0;

  @media (max-width: 768px) {
    border-left: none;
    padding: 0.75rem 0;
    gap: 2rem;
    justify-content: center;
    border-top: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
  }
`;

export const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }
`;

export const StatIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  &.points {
    background: linear-gradient(135deg, #FFF3CD 0%, #FFE69C 100%);
    color: ${COLORS.bronzeAlt};
  }

  &.badges {
    background: linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%);
    color: ${COLORS.primary};
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const StatValue = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${COLORS.text};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${COLORS.textSecondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

export const SmallActions = styled.div`
  display: flex;
  gap: 0.5rem;

  .button-text {
    display: none;
  }

  .qr-icon-button {
    svg {
      width: 1rem;
      height: 1rem;
    }
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
    padding-top: 0.75rem;

    .button-text {
      display: inline;
    }

    button, a {
      flex: 1;
      min-width: 0;
      justify-content: center;
      padding: 0.65rem 1rem !important;
      font-size: 0.875rem;
      font-weight: 500;
      border-radius: 0.5rem !important;
      transition: all 0.2s;
      height: auto !important;
      width: auto !important;
    }

    .qr-button-mobile {
      background: white !important;
      border: 1.5px solid #d1d5db !important;
      color: #374151 !important;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:hover {
        background: #f9fafb !important;
        border-color: ${COLORS.primary} !important;
        color: ${COLORS.primary} !important;
      }

      svg {
        width: 1rem;
        height: 1rem;
      }
    }

    .profile-button-mobile {
      background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%) !important;
      color: white !important;
      border: none !important;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:hover {
        background: linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primaryDarker} 100%) !important;
      }

      svg {
        width: 1rem;
        height: 1rem;
      }
    }
  }
`;

// CTA Section Styles
export const CTASection = styled.section`
  max-width: 600px;
  margin: 3rem auto;
  padding: 0 1rem;
`;

export const CTACard = styled.div`
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%);
  border-radius: 1.5rem;
  padding: 2.5rem 2rem;
  text-align: center;
  box-shadow: 0 15px 50px rgba(108, 177, 50, 0.3);
`;

export const CTATitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.75rem;
`;

export const CTAText = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0;
`;

// Badge Section Styles
export const BadgeSection = styled.section`
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin: 2rem auto;
    padding: 0 0.75rem;
  }
`;

export const BadgeSectionHeader = styled.div`
  text-align: center;
  padding: 1rem 1rem;
  background: linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.secondaryDark} 100%);
  border-radius: 1.5rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    top: -100px;
    right: -100px;
  }

  &::after {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    bottom: -50px;
    left: -50px;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin-bottom: 1.5rem;
  }
`;

export const BadgeHeaderIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const BadgeSectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const BadgeSectionSubtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

export const BadgeCategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const CategoryCard = styled.div`
  background: white;
  border-radius: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid ${COLORS.border};
  transition: box-shadow 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

export const CategoryHeader = styled.div`
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const CategoryHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

export const CategoryEmoji = styled.div`
  font-size: 1.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const CategoryTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${COLORS.text};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

export const BadgeCount = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${COLORS.primary};

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

export const CategoryDescription = styled.p`
  font-size: 0.875rem;
  color: ${COLORS.textSecondary};
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const ToggleIcon = styled.div`
  color: ${COLORS.primary};
  transition: transform 0.3s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0)'};
  flex-shrink: 0;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  @media (max-width: 768px) {
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

export const BadgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(to bottom, #f9fafb, white);
  border-top: 1px solid #f0f0f0;
  animation: slideDown 0.4s ease-out forwards;

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      max-height: 5000px;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const BadgeCard = styled.div`
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.5s ease-out forwards;
  animation-delay: calc(var(--index) * 0.05s);
  opacity: 0;

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
    from {
      opacity: 0;
      transform: translateY(30px);
    }
  }

  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 12px 32px rgba(108, 177, 50, 0.2);
    border-color: ${COLORS.primary};
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

export const PointsBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark});
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(108, 177, 50, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  ${BadgeCard}:hover & {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(108, 177, 50, 0.4);
  }

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
`;

export const BadgeImageWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin-bottom: 0.75rem;
  position: relative;
  transition: transform 0.3s ease;

  ${BadgeCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

export const BadgeImageContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BadgeImageSkeleton = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e8e8e8 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export const BadgeImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  position: relative;
  z-index: 1;
`;

export const BadgeName = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: ${COLORS.text};
  margin-bottom: 0.25rem;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const BadgeDescription = styled.p`
  font-size: 0.8rem;
  color: ${COLORS.textSecondary};
  line-height: 1.4;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

// Global Dialog Styles
export const GlobalDialogStyles = `
  .qr-dialog {
    background: white;
    border-radius: 0.75rem;
    padding: 1.5rem;
    max-width: 90vw;
    position: relative;
    z-index: 9999;
  }

  .qr-title {
    color: #1a1a2e;
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .qr-description {
    color: #64718A;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .qr-image-container {
    display: flex;
    justify-content: center;
    padding: 1.5rem;
    background: #f5f7fa;
    border-radius: 0.5rem;
    margin-top: 1rem;
  }

  .qr-image {
    max-width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 0.5rem;
  }

  .mobile-dialog {
    background: white;
    border-radius: 0.75rem;
    position: relative;
    z-index: 9999;
  }

  /* Ensure close button is visible */
  [data-radix-dialog-content] button[aria-label="Close"],
  [data-radix-dialog-content] button[data-state] {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 2rem;
    height: 2rem;
    border-radius: 0.25rem;
    background: rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10000;
    transition: all 0.2s;
  }

  [data-radix-dialog-content] button[aria-label="Close"]:hover,
  [data-radix-dialog-content] button[data-state]:hover {
    background: rgba(0, 0, 0, 0.1);
    border-color: rgba(0, 0, 0, 0.2);
  }

  [data-radix-dialog-content] button[aria-label="Close"] svg,
  [data-radix-dialog-content] button[data-state] svg {
    width: 1rem;
    height: 1rem;
    color: #1a1a2e;
  }

  /* QR Button visibility */
  .qr-button,
  .qr-icon-button,
  .qr-dialog-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
    font-weight: 500;
    transition: all 0.2s;
  }

  .qr-button:hover,
  .qr-icon-button:hover,
  .qr-dialog-button:hover {
    background: #f9fafb;
    border-color: #6CB132;
    color: #6CB132;
  }

  .qr-button svg,
  .qr-icon-button svg,
  .qr-dialog-button svg {
    width: 1rem;
    height: 1rem;
  }

  /* Dialog overlay */
  [data-radix-dialog-overlay] {
    background: rgba(0, 0, 0, 0.5);
    position: fixed;
    inset: 0;
    z-index: 9998;
  }

  /* Dialog content */
  [data-radix-dialog-content] {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-height: 90vh;
    overflow-y: auto;
    z-index: 9999;
  }

  @media (max-width: 640px) {
    [data-radix-dialog-content] {
      max-width: 90vw;
      width: 90vw;
    }
  }
`;

// Badge Display Section Styles
export const BadgesContainer = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(229, 231, 235, 0.6);
`;

export const BadgesSectionHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${COLORS.textSecondary};
  margin-bottom: 0.625rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  svg {
    width: 0.875rem;
    height: 0.875rem;
    color: ${COLORS.secondary};
  }
`;

export const BadgesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: ${props => props.$expanded ? '400px' : '200px'};
  transition: max-height 0.3s ease-out;
`;

export const BadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border: 1.5px solid ${COLORS.border};
  border-radius: 0.625rem;
  padding: 0.5rem 0.625rem;
  transition: all 0.2s ease;
  cursor: pointer;
  animation: badgeSlideIn 0.3s ease-out forwards;
  animation-delay: ${props => props.$index * 0.1}s;
  opacity: 0;

  @keyframes badgeSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:hover {
    border-color: rgba(108, 177, 50, 0.3);
    box-shadow: 0 2px 8px rgba(108, 177, 50, 0.15);
    transform: translateX(2px);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.5rem;
    gap: 0.5rem;
  }
`;

export const BadgeItemImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(108, 177, 50, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

export const BadgeItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const BadgeItemName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

export const BadgeItemDescription = styled.div`
  font-size: 0.7rem;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

export const ShowMoreButton = styled.button`
  background: transparent;
  color: ${COLORS.primary};
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.375rem 0.625rem;
  border: 1px dashed rgba(108, 177, 50, 0.4);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.25rem;
  width: 100%;
  text-align: center;

  &:hover {
    background: rgba(108, 177, 50, 0.05);
    border-style: solid;
  }

  &:active {
    transform: scale(0.98);
  }
`;

// Rising Stars Badge Section (Horizontal Layout)
export const RisingStarsBadgeSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  padding: 0.75rem 0 0 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 0.5rem;
  width: 100%;
  flex-wrap: wrap;
  grid-column: 1 / -1;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 0;
  }
`;

export const CompactBadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border: 1px solid ${COLORS.border};
  border-radius: 0.625rem;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 0;

  &:hover {
    background: rgba(108, 177, 50, 0.04);
    border-color: rgba(108, 177, 50, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    flex: 1 1 100%;
  }
`;

export const CompactBadgeImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid rgba(108, 177, 50, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`;

export const CompactBadgeText = styled.div`
  flex: 1;
  min-width: 0;

  .badge-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: ${COLORS.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  .badge-desc {
    font-size: 0.7rem;
    color: ${COLORS.textMuted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
    margin-top: 0.125rem;
  }

  @media (max-width: 768px) {
    .badge-name {
      font-size: 0.7rem;
    }
    .badge-desc {
      font-size: 0.65rem;
    }
  }
`;

export const BadgeMoreText = styled.span`
  font-size: 0.7rem;
  color: ${COLORS.primary};
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
`;

export const RisingStarsBadgeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;

  .badge-header-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: ${COLORS.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    color: ${COLORS.textMuted};
    cursor: help;
    transition: color 0.2s ease;

    &:hover {
      color: ${COLORS.primary};
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

// Inject global styles utility
export const injectGlobalStyles = () => {
  if (typeof document !== 'undefined') {
    const styleId = 'qr-dialog-global-styles';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = GlobalDialogStyles;
      document.head.appendChild(styleElement);
    }
  }
};
